import React from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as ProfitCenterIcon } from 'assets/icons/profitCenter.svg';
import { ReactComponent as TrashIcon } from 'assets/icons/trash.svg';

import { IS_VC_UNIT_CARD_DETAILS_VISIBLE } from '../../../../../../constants/general';
import { formatRevenueAndPercentage } from '../../../../../../utils/helpers';
import CustomNodeItem from '../../BusinessStructure/components/CustomNodeItem';

import type { IProfitCentreNode } from '../interfaces/IProfitCentreNode';

import styles from '../sass/CustomUnitNode.module.scss';

function ProfitCentreNode({
  name,
  netBookValue,
  totalRevenueRolling12MActual,
  totalRevenueRolling12MPercents,
  totalGrossMarginRolling12MPercents,
  totalGrossMarginRolling12MActual,
  wipFinishedGoodsStock,
  uniqueIdentifier,
  naceSection,
  addressCountry,
  virtualProperties,
  isCollapsed,
  isDraft,
  showDraftDeleteButton = true,
  cardsInsideCount,
  onDraftDelete,
}: IProfitCentreNode) {
  const { t } = useTranslation();

  const sumCalcValue = (Number(virtualProperties?.assetValue?.[uniqueIdentifier]?.split(' ')[0]) || 0)
    + (Number(virtualProperties?.implicitAssetValue?.[uniqueIdentifier]?.split(' ')[0]) || 0);

  const customNodeItems = [
    {
      title: t('valueChainMap.profitCenterId'),
      value: uniqueIdentifier || '-',
    },
    {
      title: t('valueChainMap.naceSection'),
      value: naceSection || '-',
    },
    {
      title: t('valueChainMap.addressCountry'),
      value: addressCountry || '-',
    },
    {
      title: t('valueChainMap.balanceSheetAsset'),
      value: formatRevenueAndPercentage({
        actual: netBookValue?.amount,
        currency: netBookValue?.currency,
      }),
      calculateValue: formatRevenueAndPercentage({
        actual: sumCalcValue,
        currency: netBookValue?.currency,
      }),
    },
    {
      title: t('valueChainMap.actualRevenue'),
      value: formatRevenueAndPercentage({
        actual: totalRevenueRolling12MActual?.amount,
        currency: totalRevenueRolling12MActual?.currency,
        percentage: totalRevenueRolling12MPercents?.toString(),
      }),

    },
    {
      title: t('valueChainMap.actualGrossMargin'),
      value: formatRevenueAndPercentage({
        actual: totalGrossMarginRolling12MActual?.amount,
        currency: totalGrossMarginRolling12MActual?.currency,
        percentage: totalGrossMarginRolling12MPercents?.toString(),
      }),
    },
    {
      title: t('valueChainMap.rawMaterialWipFinishedGoodsStock'),
      value: formatRevenueAndPercentage({
        actual: wipFinishedGoodsStock?.amount,
        currency: wipFinishedGoodsStock?.currency,
      }),
    },
  ];

  return (
    <>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <ProfitCenterIcon />
          <p className={styles.title}>{name}</p>
        </div>
        <div className={styles.headerRight}>
          {isCollapsed && cardsInsideCount && cardsInsideCount > 0 ? (
            <span className={styles.cardsCount}>{cardsInsideCount}</span>
          ) : null}
          {isDraft && showDraftDeleteButton ? (
            <button
              type="button"
              className={styles.draftDeleteButton}
              aria-label={t('common.delete')}
              onClick={(event) => {
                event.stopPropagation();
                onDraftDelete?.();
              }}
            >
              <TrashIcon className={styles.draftDeleteIcon} />
            </button>
          ) : null}
        </div>
      </div>
      {IS_VC_UNIT_CARD_DETAILS_VISIBLE && !isCollapsed ? (
        <div className={styles.content}>
          {customNodeItems.map(({ title, value, calculateValue }) => (
            <CustomNodeItem
              key={`customNodeItems_${title}`}
              title={title}
              value={value}
              calculateValue={calculateValue}
            />
          ))}
        </div>
      ) : null}
    </>
  );
}

export default ProfitCentreNode;
